import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {API_URL} from '@aviary/injection-tokens/app-provided.tokens';
import {map, Observable, OperatorFunction} from 'rxjs';
import {
  ApiContractError,
  DeleteOptions,
  GetOptions,
  isListDataOptions,
  ListDataOptions,
  ListDataOptionsOrSimply,
  PatchOptions,
  PostOptions,
  PutOptions
} from '@aviary/services/api.service.types';
import {
  MxResponseBase,
  MxResponseBase2,
  MxResponseBaseWithObjects,
  MxResponseBaseWithPaginationData,
  MxResponseBaseWithPopulatedObjects
} from '@aviary/store/common/response-base.types';
import {isArray, isNull, isNumber, isPopulatedArray, isUndefined} from '@aviary/store/common/typing.helpers';
import {FacetExtractors, ListData} from '@aviary/store/common/common.types';
import {extractListDataWithoutPaginationInfo, extractListDataWithPaginationInfo} from '@aviary/store/common/list-data.helpers';

@Injectable({providedIn: 'root'})
export class ApiService {

  private readonly http: HttpClient = inject(HttpClient);
  private readonly apiUrl: string = inject(API_URL);

  // Ensure the API URL ends with a trailing slash so relative endpoint paths properly append to existing paths (such as "/api").
  private readonly normalizedApiUrl: string = this.apiUrl.endsWith('/') ? this.apiUrl : `${this.apiUrl}/`;

  ////
  //// DELETE
  ////
  public delete<ResponseType>(endpointPath: string, options?: DeleteOptions): Observable<ResponseType> {
    return this.http.delete<ResponseType>(this.urlFor(endpointPath), options);
  }

  public deleteAndReturnNothing(endpointPath: string, options?: DeleteOptions): Observable<void> {
    return this.delete<MxResponseBase2<void>>(endpointPath, options).pipe(
      this.mapToNothing()
    );
  }

  ////
  //// GET
  ////
  public get<ResponseType>(endpointPath: string, options?: GetOptions): Observable<ResponseType> {
    return this.http.get<ResponseType>(this.urlFor(endpointPath), options);
  }

  public getObjects<ObjectType>(endpointPath: string, options?: GetOptions): Observable<ObjectType[]> {
    return this.get<MxResponseBase2<ObjectType>>(endpointPath, options).pipe(
      this.mapToObjects(endpointPath)
    );
  }

  public getOptionalObjects<ObjectType>(endpointPath: string, options?: GetOptions): Observable<ObjectType[]> {
    return this.get<MxResponseBase2<ObjectType>>(endpointPath, options).pipe(
      this.mapToOptionalObjects()
    );
  }

  public getSingleObject<ObjectType>(endpointPath: string, options?: GetOptions): Observable<ObjectType> {
    return this.get<MxResponseBase2<ObjectType>>(endpointPath, options).pipe(
      this.mapToSingleObject(endpointPath)
    );
  }

  public getOptionalSingleObject<ObjectType>(endpointPath: string, options?: GetOptions): Observable<ObjectType | undefined> {
    return this.get<MxResponseBase2<ObjectType>>(endpointPath, options).pipe(
      this.mapToOptionalSingleObject()
    );
  }

  public getListData<ObjectType>(endpointPath: string, httpOptions?: GetOptions): Observable<ListData<ObjectType>>;
  public getListData<ObjectType>(endpointPath: string, listDataOptions?: ListDataOptions<GetOptions>): Observable<ListData<ObjectType>>;
  public getListData<ObjectType>(endpointPath: string, listDataOrHttpOptions?: ListDataOptionsOrSimply<GetOptions>): Observable<ListData<ObjectType>> {
    const options: ListDataOptions<GetOptions> = this.asListDataOptions(listDataOrHttpOptions);

    return this.get<MxResponseBase2<ObjectType>>(endpointPath, options.httpOptions).pipe(
      this.mapToListData(endpointPath, options)
    );
  }

  ////
  //// PATCH
  ////
  public patch<ResponseType, BodyType>(endpointPath: string, body: BodyType, options?: PatchOptions): Observable<ResponseType> {
    return this.http.patch<ResponseType>(this.urlFor(endpointPath), body, options);
  }

  public patchWithoutBody<ResponseType>(endpointPath: string, options?: PatchOptions): Observable<ResponseType> {
    return this.http.patch<ResponseType>(this.urlFor(endpointPath), {}, options);
  }

  public patchAndReturnSingleObject<ObjectType, BodyType>(endpointPath: string, body: BodyType, options?: PatchOptions): Observable<ObjectType> {
    return this.patch<MxResponseBase2<ObjectType>, BodyType>(endpointPath, body, options).pipe(
      this.mapToSingleObject(endpointPath)
    );
  }

  public patchWithoutBodyAndReturnSingleObject<ObjectType, >(endpointPath: string, options?: PatchOptions): Observable<ObjectType> {
    return this.patchWithoutBody<MxResponseBase2<ObjectType>>(endpointPath, options).pipe(
      this.mapToSingleObject(endpointPath)
    );
  }

  ////
  //// POST
  ////
  public post<ResponseType, BodyType>(endpointPath: string, body: BodyType, options?: PostOptions): Observable<ResponseType> {
    return this.http.post<ResponseType>(this.urlFor(endpointPath), body, options);
  }

  public postAndReturnNothing<BodyType>(endpointPath: string, body: BodyType, options?: PostOptions): Observable<void> {
    return this.post<MxResponseBase2<void>, BodyType>(endpointPath, body, options).pipe(
      this.mapToNothing()
    );
  }

  public postAndReturnObjects<ObjectType, BodyType>(endpointPath: string, body: BodyType, options?: PostOptions): Observable<ObjectType[]> {
    return this.post<MxResponseBase2<ObjectType>, BodyType>(endpointPath, body, options).pipe(
      this.mapToObjects(endpointPath)
    );
  }

  public postAndReturnSingleObject<ObjectType, BodyType>(endpointPath: string, body: BodyType, options?: PostOptions): Observable<ObjectType> {
    return this.post<MxResponseBase2<ObjectType>, BodyType>(endpointPath, body, options).pipe(
      this.mapToSingleObject(endpointPath)
    );
  }

  public postAndReturnListData<ObjectType, BodyType>(endpointPath: string, body: BodyType, httpOptions?: PostOptions): Observable<ListData<ObjectType>>;
  public postAndReturnListData<ObjectType, BodyType>(endpointPath: string, body: BodyType, listDataOptions?: ListDataOptions<PostOptions>): Observable<ListData<ObjectType>>;
  public postAndReturnListData<ObjectType, BodyType>(endpointPath: string, body: BodyType, listDataOrHttpOptions?: ListDataOptionsOrSimply<PostOptions>): Observable<ListData<ObjectType>> {
    const options: ListDataOptions<PostOptions> = this.asListDataOptions(listDataOrHttpOptions);

    return this.post<MxResponseBase2<ObjectType>, BodyType>(endpointPath, body, options.httpOptions).pipe(
      this.mapToListData(endpointPath, options)
    );
  }

  public postWithoutBody<ResponseType>(endpointPath: string, options?: PostOptions): Observable<ResponseType> {
    return this.http.post<ResponseType>(this.urlFor(endpointPath), {}, options);
  }

  public postWithoutBodyAndReturnListData<ObjectType>(endpointPath: string, httpOptions?: PostOptions): Observable<ListData<ObjectType>>;
  public postWithoutBodyAndReturnListData<ObjectType>(endpointPath: string, listDataOptions?: ListDataOptions<PostOptions>): Observable<ListData<ObjectType>>;
  public postWithoutBodyAndReturnListData<ObjectType>(endpointPath: string, listDataOrHttpOptions?: ListDataOptionsOrSimply<PostOptions>): Observable<ListData<ObjectType>> {
    const options: ListDataOptions<PostOptions> = this.asListDataOptions(listDataOrHttpOptions);

    return this.postWithoutBody<MxResponseBase2<ObjectType>>(endpointPath, options.httpOptions).pipe(
      this.mapToListData(endpointPath, options)
    );
  }

  public postWithoutBodyAndReturnOptionalListData<ObjectType>(endpointPath: string, httpOptions?: PostOptions): Observable<ListData<ObjectType>>;
  public postWithoutBodyAndReturnOptionalListData<ObjectType>(endpointPath: string, listDataOptions?: ListDataOptions<PostOptions>): Observable<ListData<ObjectType>>;
  public postWithoutBodyAndReturnOptionalListData<ObjectType>(endpointPath: string, listDataOrHttpOptions?: ListDataOptionsOrSimply<PostOptions>): Observable<ListData<ObjectType>> {
    const options: ListDataOptions<PostOptions> = this.asListDataOptions(listDataOrHttpOptions);

    return this.postWithoutBody<MxResponseBase2<ObjectType>>(endpointPath, options.httpOptions).pipe(
      this.mapToOptionalListData(endpointPath, options)
    );
  }

  ////
  //// PUT
  ////
  public put<ResponseType, BodyType>(endpointPath: string, body: BodyType, options?: PutOptions): Observable<ResponseType> {
    return this.http.put<ResponseType>(this.urlFor(endpointPath), body, options);
  }

  public putWithoutBody<ResponseType>(endpointPath: string, options?: PutOptions): Observable<ResponseType> {
    return this.http.put<ResponseType>(this.urlFor(endpointPath), {}, options);
  }

  public putAndReturnSingleObject<ObjectType, BodyType>(endpointPath: string, body: BodyType, options?: PutOptions): Observable<ObjectType> {
    return this.put<MxResponseBase2<ObjectType>, BodyType>(endpointPath, body, options).pipe(
      this.mapToSingleObject(endpointPath)
    );
  }

  ////
  //// END OF PUBLIC INTERFACE
  ////

  private urlFor(endpointPath: string): string {
    // If there's a leading slash, drop it because it would cause the endpoint to overwrite any existing path (such as "/api") instead of appending to it.
    const normalizedEndpointPath: string = endpointPath.replace(/^\/+/, '');

    return new URL(normalizedEndpointPath, this.normalizedApiUrl).toString();
  }

  private asListDataOptions<OptionsType>(listDataOrHttpOptions?: ListDataOptionsOrSimply<OptionsType>): ListDataOptions<OptionsType> {
    return isListDataOptions(listDataOrHttpOptions) ? listDataOrHttpOptions : {httpOptions: listDataOrHttpOptions};
  }

  private mapToNothing(): OperatorFunction<MxResponseBase2<void>, void> {
    return map(() => undefined);
  }

  private mapToObjects<ObjectType>(endpointPath: string): OperatorFunction<MxResponseBase2<ObjectType>, ObjectType[]> {
    return map(response => {
      this.assertHasObjects(response, endpointPath);

      return response.objects;
    });
  }

  private mapToOptionalObjects<ObjectType>(): OperatorFunction<MxResponseBase2<ObjectType>, ObjectType[]> {
    return map(response => response.objects ?? []);
  }


  private mapToSingleObject<ObjectType>(endpointPath: string): OperatorFunction<MxResponseBase2<ObjectType>, ObjectType> {
    return map(response => {
      this.assertHasPopulatedObjects(response, endpointPath);

      return response.objects[0];
    });
  }

  private mapToOptionalSingleObject<ObjectType>(): OperatorFunction<MxResponseBase2<ObjectType>, ObjectType | undefined> {
    return map(response => response.objects?.[0]);
  }

  private mapToListData<ObjectType, OptionsType>(
    endpointPath: string, options: ListDataOptions<OptionsType>
  ): OperatorFunction<MxResponseBase2<ObjectType>, ListData<ObjectType>> {
    return map(response => {
      this.assertHasObjects(response, endpointPath);

      const includePaginationInfo: boolean = options.includePaginationInfo ?? true;
      const facetExtractors: FacetExtractors = options.facetExtractors ?? [];

      if (includePaginationInfo) {
        this.assertHasPaginationData(response, endpointPath);

        return extractListDataWithPaginationInfo(response as MxResponseBase<ObjectType>, facetExtractors);
      }

      return extractListDataWithoutPaginationInfo(response as MxResponseBase<ObjectType>, facetExtractors);
    });
  }

  private mapToOptionalListData<ObjectType, OptionsType>(
    endpointPath: string, options: ListDataOptions<OptionsType>
  ): OperatorFunction<MxResponseBase2<ObjectType>, ListData<ObjectType>> {
    return map(response => {
      const includePaginationInfo: boolean = options.includePaginationInfo ?? true;
      const facetExtractors: FacetExtractors = options.facetExtractors ?? [];

      if (isPopulatedArray(response.objects) && includePaginationInfo) {
        this.assertHasPaginationData(response, endpointPath);

        return extractListDataWithPaginationInfo(response as MxResponseBase<ObjectType>, facetExtractors);
      }

      return extractListDataWithoutPaginationInfo(response as MxResponseBase<ObjectType>, facetExtractors);
    });
  }

  private assertHasObjects<ObjectType>(
    response: MxResponseBase2<ObjectType>, endpointPath: string
  ): asserts response is MxResponseBaseWithObjects<ObjectType> {
    if (!isArray(response.objects)) {
      this.throwApiContractError(
        `Expected response.objects to be an array for endpoint "${endpointPath}", but received ${this.describeValue(response.objects)}.`
      );
    }
  }

  private assertHasPopulatedObjects<ObjectType>(
    response: MxResponseBase2<ObjectType>, endpointPath: string
  ): asserts response is MxResponseBaseWithPopulatedObjects<ObjectType> {
    this.assertHasObjects(response, endpointPath);

    if (!isPopulatedArray(response.objects)) {
      this.throwApiContractError(`Expected response.objects to have at least one element for endpoint "${endpointPath}", but it was empty.`);
    }
  }

  private assertHasPaginationData<ObjectType>(
    response: MxResponseBase2<ObjectType>, endpointPath: string
  ): asserts response is MxResponseBaseWithPaginationData<ObjectType> {
    for (const fieldName of ['page', 'limit', 'totalCount'] as const) {
      const value: unknown = response[fieldName];

      if (!isNumber(value)) {
        this.throwApiContractError(`Expected response.${fieldName} to be a number for endpoint "${endpointPath}", but received ${this.describeValue(value)}.`);
      }
    }
  }

  private throwApiContractError(message: string): never {
    const error: ApiContractError = new ApiContractError(message);

    // eslint-disable-next-line no-console -- This is temporary.  We should hook this up to Sentry.
    console.error(error);

    throw error;
  }

  private describeValue(value: unknown): string {
    if (isUndefined(value)) return 'undefined';
    if (isNull(value)) return 'null';
    if (isArray(value)) return 'an array';

    return typeof value;
  }
}
